// Runtime app settings: fetched from this app's own origin once, cached in a
// module-scoped variable, kept off the Module Federation runtime path so this
// module (imported all over the app) never drags that runtime in.
//
// Read settings live, at the point of use — `(await loadAppSettings()).X` —
// never into a module-scope constant. A module-scope snapshot taken before the
// fetch resolves captures undefined permanently, and with three MF exposes any
// of them can load first (see docs/superpowers/specs/2026-09-28-runtime-appsettings-design.md).

/** App-facing settings shape (friendly names; wire keys never leak past this module). */
export interface AppSettings {
  AUTHENTICATION_AUTHORITY?: string;
  CLIENT_ID?: string;
  CLIENT_SCOPE?: string;
  SETTINGS_API_BASE_URL?: string;
  COREAPI_BASE_URL?: string;
  APPINSIGHTS_CONNECTIONSTRING?: string;
  APPINSIGHTS_ENABLED?: boolean;
}

// Wire keys keep their VITE_ prefix even though Vite no longer statically
// replaces them: the ADO pipeline's patch step substitutes these from Vault by
// matching pipeline-variable name to JSON key, and those variables are named
// after the pre-migration .env keys (see pipelines/AdminUI/*-cicd-pipeline.yml).
export interface RawAppSettings {
  VITE_AUTHENTICATION_AUTHORITY?: string;
  VITE_CLIENT_ID?: string;
  VITE_CLIENT_SCOPE?: string;
  VITE_SETTINGS_API_BASE_URL?: string;
  VITE_COREAPI_BASE_URL?: string;
  VITE_ConnectionString?: string;
  VITE_InstrumentationEnabled?: boolean | string;
}

/** Map the wire-format appsettings.json to the app-facing shape. */
export function mapToAppSettings(raw: RawAppSettings): AppSettings {
  return {
    AUTHENTICATION_AUTHORITY: raw.VITE_AUTHENTICATION_AUTHORITY,
    CLIENT_ID: raw.VITE_CLIENT_ID,
    CLIENT_SCOPE: raw.VITE_CLIENT_SCOPE,
    SETTINGS_API_BASE_URL: raw.VITE_SETTINGS_API_BASE_URL,
    COREAPI_BASE_URL: raw.VITE_COREAPI_BASE_URL,
    APPINSIGHTS_CONNECTIONSTRING: raw.VITE_ConnectionString,
    APPINSIGHTS_ENABLED:
      raw.VITE_InstrumentationEnabled === true || raw.VITE_InstrumentationEnabled === "true",
  };
}

let cache: AppSettings | null = null;
let pending: Promise<AppSettings> | null = null;

/**
 * Cached app settings, or {} before loadAppSettings() resolves. Sync, live
 * read — only for callers that can't await (getAuthService's construction).
 */
export function getAppSettings(): AppSettings {
  return cache ?? {};
}

/** Test/host seam: set the cache; pass null to force a refetch. */
export function setAppSettings(value: AppSettings | null): void {
  cache = value;
}

async function fetchAppSettings(): Promise<RawAppSettings> {
  // The remote's own origin, not the host's: appsettings.json sits at this
  // app's deployment root, one level above the chunk this module is bundled
  // into (build.assetsDir defaults to "assets"; in dev the module is served
  // from /src/). Deliberately not `new URL(".", import.meta.url)` — Vite
  // treats a static "." argument as an asset reference and rewrites it at
  // build time. Splitting the string keeps this a plain runtime computation.
  const moduleUrl = import.meta.url;
  const baseUrl = new URL(`${moduleUrl.slice(0, moduleUrl.lastIndexOf("/") + 1)}../`).href;

  const response = await fetch(`${baseUrl}appsettings.json`, { cache: "no-store" });
  if (!response.ok) throw new Error(`appsettings.json ${response.status}`);
  const data: unknown = await response.json();
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    throw new Error("appsettings.json is not a JSON object");
  }
  return data as RawAppSettings;
}

/**
 * Warm cache, or fetch + map + cache appsettings.json. Concurrent callers
 * share one in-flight fetch. Fails soft to {} with cache left null so the
 * next call retries.
 */
export async function loadAppSettings(): Promise<AppSettings> {
  if (cache) return cache;
  if (pending) return pending;

  pending = (async () => {
    try {
      cache = mapToAppSettings(await fetchAppSettings());
      return cache;
    } catch (error) {
      console.error("[admin-ui] failed to load appsettings.json", error);
      return {};
    } finally {
      pending = null;
    }
  })();

  return pending;
}
